/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import { isString } from '../../services/predicate';
import { EuiContextMenuItem, EuiContextMenuPanel } from '../context_menu';
import { EuiPopover } from '../popover';
import { EuiButtonIcon } from '../button';
import { EuiToolTip } from '../tool_tip';
import { EuiI18n } from '../i18n';

function actionIsCustomItemAction(action) {
  return action.hasOwnProperty('render');
}

export class CollapsedItemActions extends Component {
  popoverDiv = null;
  state = {
    popoverOpen: false
  };
  togglePopover = () => {
    this.setState(prevState => ({
      popoverOpen: !prevState.popoverOpen
    }));
  };
  closePopover = () => {
    this.setState({
      popoverOpen: false
    });
  };
  onPopoverBlur = () => {
    // you must be asking... WTF? I know... but this timeout is
    // required to make sure we process the onBlur events after the initial
    // event cycle. Reference:
    // https://medium.com/@jessebeach/dealing-with-focus-and-blur-in-a-composite-widget-in-react-90d3c3b49a9b
    window.requestAnimationFrame(() => {
      if (!this.popoverDiv.contains(document.activeElement) && this.props.onBlur) {
        this.props.onBlur();
      }
    });
  };
  registerPopoverDiv = popoverDiv => {
    if (!this.popoverDiv) {
      this.popoverDiv = popoverDiv;
      this.popoverDiv.addEventListener('focusout', this.onPopoverBlur);
    }
  };

  componentWillUnmount() {
    if (this.popoverDiv) {
      this.popoverDiv.removeEventListener('focusout', this.onPopoverBlur);
    }
  }

  onClickItem = onClickAction => {
    this.closePopover();

    if (onClickAction) {
      onClickAction();
    }
  };

  render() {
    const {
      actions,
      itemId,
      item,
      actionEnabled,
      onFocus,
      className
    } = this.props;
    const isOpen = this.state.popoverOpen;
    let allDisabled = true;
    const controls = actions.reduce((controls, action, index) => {
      const key = `action_${itemId}_${index}`;
      const available = action.available ? action.available(item) : true;

      if (!available) {
        return controls;
      }

      const enabled = actionEnabled(action);
      allDisabled = allDisabled && !enabled;

      if (actionIsCustomItemAction(action)) {
        const customAction = action;
        const actionControl = customAction.render(item, enabled);
        const actionControlOnClick = actionControl && actionControl.props && actionControl.props.onClick;
        controls.push(<EuiContextMenuItem key={key} onClick={() => this.onClickItem(actionControlOnClick ? () => actionControlOnClick(item) : undefined)}>
              {actionControl}
            </EuiContextMenuItem>);
      } else {
        const {
          onClick,
          name,
          href,
          target,
          'data-test-subj': dataTestSubj
        } = action;
        const buttonIcon = action.icon;
        let icon;

        if (buttonIcon) {
          icon = isString(buttonIcon) ? buttonIcon : buttonIcon(item);
        }

        const buttonContent = typeof name === 'function' ? name(item) : name;
        controls.push(<EuiContextMenuItem key={key} disabled={!enabled} href={href} target={target} icon={icon} data-test-subj={dataTestSubj} onClick={() => this.onClickItem(onClick ? () => onClick(item) : undefined)}>
              {buttonContent}
            </EuiContextMenuItem>);
      }

      return controls;
    }, []);
    const popoverButton = <EuiI18n token="euiCollapsedItemActions.allActions" default="All actions">
        {allActions => <EuiButtonIcon className={className} aria-label={allActions} iconType="boxesHorizontal" color="text" isDisabled={allDisabled} onClick={this.togglePopover.bind(this)} onFocus={onFocus} data-test-subj="euiCollapsedItemActionsButton" />}
      </EuiI18n>;
    const withTooltip = !allDisabled && <EuiI18n token="euiCollapsedItemActions.allActions" default="All actions">
        {allActions => <EuiToolTip content={allActions} delay="long">
            {popoverButton}
          </EuiToolTip>}
      </EuiI18n>;
    return <EuiPopover className={className} popoverRef={this.registerPopoverDiv} id={`${itemId}-actions`} isOpen={isOpen} button={withTooltip || popoverButton} closePopover={this.closePopover} panelPaddingSize="none" anchorPosition="leftCenter">
        <EuiContextMenuPanel items={controls} />
      </EuiPopover>;
  }

}
CollapsedItemActions.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    /**
       * The type of action
       */
    type: PropTypes.oneOfType([PropTypes.oneOf(["button"]), PropTypes.oneOf(["icon"]).isRequired]),

    /**
       * Defines the color of the button
       */
    color: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["primary", "danger", "text", "ghost", "success", "warning"]).isRequired, PropTypes.func.isRequired]), PropTypes.oneOfType([PropTypes.oneOf(["primary", "accent", "success", "warning", "danger", "ghost", "text"]).isRequired, PropTypes.func.isRequired])]),

    /**
       * The display name of the action (will be the button caption)
       */

    /**
       * The display name of the action (will be the button caption)
       */
    name: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.func.isRequired]).isRequired,

    /**
       * Describes the action (will be the button title)
       */

    /**
       * Describes the action (will be the button title)
       */
    description: PropTypes.string.isRequired,

    /**
       * A handler function to execute the action
       */

    /**
       * A handler function to execute the action
       */
    onClick: PropTypes.func,
    href: PropTypes.string,
    target: PropTypes.string,

    /**
       * A callback function that determines whether the action is available
       */

    /**
       * A callback function that determines whether the action is available
       */
    available: PropTypes.func,

    /**
       * A callback function that determines whether the action is enabled
       */

    /**
       * A callback function that determines whether the action is enabled
       */
    enabled: PropTypes.func,
    isPrimary: PropTypes.bool,
    "data-test-subj": PropTypes.string,

    /**
       * Associates an icon with the button
       */
    icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "alert", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "codeApp", "color", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crossInACircleFilled", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "document", "documentEdit", "documentation", "documents", "dot", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenTag", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.func.isRequired])
  }).isRequired, PropTypes.shape({
    /**
       * The function that renders the action. Note that the returned node is expected to have `onFocus` and `onBlur` functions
       */
    render: PropTypes.func.isRequired,

    /**
       * A callback that defines whether the action is available
       */
    available: PropTypes.func,

    /**
       * A callback that defines whether the action is enabled
       */
    enabled: PropTypes.func,
    isPrimary: PropTypes.bool
  }).isRequired]).isRequired).isRequired,
  item: PropTypes.any.isRequired,
  itemId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
  actionEnabled: PropTypes.func.isRequired,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
};

try {
  CollapsedItemActions.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/basic_table/collapsed_item_actions.tsx',
    description: '',
    displayName: 'CollapsedItemActions',
    methods: [],
    props: {
      actions: {
        defaultValue: null,
        description: '',
        name: 'actions',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: 'Action<T>[]'
        }
      },
      item: {
        defaultValue: null,
        description: '',
        name: 'item',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: 'T'
        }
      },
      itemId: {
        defaultValue: null,
        description: '',
        name: 'itemId',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: 'ItemIdResolved'
        }
      },
      actionEnabled: {
        defaultValue: null,
        description: '',
        name: 'actionEnabled',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: '(action: Action<T>) => boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<Element, Element>) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      }
    },
    extendedInterfaces: ['CollapsedItemActionsProps']
  };
} catch (e) {}